import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { FiInfo, FiBarChart2, FiPieChart, FiList } from "react-icons/fi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Chart from "react-apexcharts";
import { BASE_URL } from "../../../../../BaseUrl";
import { getCookie } from "../../../../../functions/Functions";
import "./FinanceDashboard.css";

const formatNumber = (val) => {
  if (val === null || val === undefined) return "0.00";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const AssetsVsLiabilitiesWidget = ({ startDate, endDate, branch }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewType, setViewType] = useState("bar"); // "bar" | "donut" | "list"

  const fetchData = async () => {
    setIsLoading(true);
    const fromDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().subtract(30, "days").format("YYYY-MM-DD");
    const toDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    const payload = { fromDate, toDate };
    if (branch) {
      if (Array.isArray(branch) && branch.length > 0) {
        payload.branch_id = branch.map((b) => b.id);
      } else if (branch?.id) {
        payload.branch_id = branch.id;
      }
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/widgets/accounting-assets-vs-liabilities`,
        payload,
        {
          headers: { Authorization: `Bearer ${getCookie()}` },
        }
      );
      if (response?.data?.data) {
        setData(response.data.data);
      } else if (response?.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching accounting assets vs liabilities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, branch]);

  const totalAssets = Number(data?.total_assets) || 0;
  const totalLiabilities = Number(data?.total_liabilities) || 0;
  const ownersEquity = Number(data?.owners_equity) || 0;

  const maxVal = Math.max(totalAssets, totalLiabilities, ownersEquity, 1);

  const assetsPct = Math.min(100, Math.max(2, (totalAssets / maxVal) * 100));
  const liabilitiesPct = Math.min(100, Math.max(2, (totalLiabilities / maxVal) * 100));
  const equityPct = Math.min(100, Math.max(2, (ownersEquity / maxVal) * 100));

  const capitalEquity = data?.equity_details?.capital_equity ?? 0;
  const retainedEarnings = data?.equity_details?.retained_earnings ?? 0;

  const equityRatio = totalAssets > 0 ? ((ownersEquity / totalAssets) * 100).toFixed(1) : "0.0";
  const debtRatio = totalAssets > 0 ? ((totalLiabilities / totalAssets) * 100).toFixed(1) : "0.0";

  // ApexCharts Configurations
  const chartCategories = ["Total Assets", "Total Liabilities", "Owner's Equity"];
  const chartValues = [totalAssets, totalLiabilities, ownersEquity];
  const chartColors = ["#16a34a", "#3f88ffff", "#f59e0b"];

  const seriesBar = [
    {
      name: "Amount (Rs)",
      data: chartValues,
    },
  ];

  const optionsBar = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: "56%",
        distributed: true,
      },
    },
    colors: chartColors,
    labels: chartCategories,
    xaxis: {
      categories: chartCategories,
      labels: {
        style: { colors: "#6b7280", fontSize: "11px", fontWeight: 500 },
        formatter: (val) => `Rs ${formatNumber(val)}`,
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#374151", fontSize: "12px", fontWeight: 600 },
      },
    },
    grid: {
      strokeDashArray: 4,
      borderColor: "#e5e7eb",
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: { colors: ["#1f2937"], fontSize: "11px", fontWeight: 600 },
      formatter: (val) => `Rs ${formatNumber(val)}`,
      offsetX: 8,
    },
    states: {
      hover: {
        filter: { type: "darken", value: 0.92 },
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const cat = chartCategories[dataPointIndex] || "Metric";
        const val = series[seriesIndex][dataPointIndex] || 0;
        const color = chartColors[dataPointIndex] || "#2563eb";
        const pct = totalAssets > 0 ? ((val / totalAssets) * 100).toFixed(1) : "0.0";
        return `
          <div style="background:#ffffff; padding:10px 14px; border-radius:8px; box-shadow:0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); border:1px solid #e5e7eb; font-size:12px; font-family:inherit;">
            <div style="display:flex; align-items:center; gap:6px; font-weight:700; color:#1f2937; margin-bottom:6px;">
              <span style="width:10px; height:10px; border-radius:50%; background-color:${color}; display:inline-block;"></span>
              ${cat}
            </div>
            <div style="color:#4b5563; margin-bottom:2px;">Amount: <strong style="color:#111827; font-weight:700;">Rs ${formatNumber(val)}</strong></div>
            <div style="color:#6b7280; font-size:11px;">Relative to Assets: <strong style="color:#2563eb;">${pct}%</strong></div>
          </div>
        `;
      },
    },
  };

  const seriesDonut = [totalLiabilities, ownersEquity];
  const optionsDonut = {
    chart: {
      type: "donut",
      fontFamily: "inherit",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },
    labels: ["Total Liabilities", "Owner's Equity"],
    colors: ["#1e293b", "#f59e0b"],
    legend: {
      position: "right",
      fontSize: "12px",
      fontWeight: 500,
      markers: { width: 10, height: 10, radius: 5 },
      itemMargin: { vertical: 4 },
      formatter: (seriesName, opts) => {
        const val = opts.w.globals.series[opts.seriesIndex];
        return `${seriesName}: Rs ${formatNumber(val)}`;
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const label = w.globals.labels[seriesIndex] || "Category";
        const val = series[seriesIndex] || 0;
        const color = chartColors[seriesIndex + 1] || "#2563eb";
        const pct = totalAssets > 0 ? ((val / totalAssets) * 100).toFixed(1) : "0.0";
        return `
          <div style="background:#ffffff; padding:10px 14px; border-radius:8px; box-shadow:0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); border:1px solid #e5e7eb; font-size:12px; font-family:inherit;">
            <div style="display:flex; align-items:center; gap:6px; font-weight:700; color:#1f2937; margin-bottom:6px;">
              <span style="width:10px; height:10px; border-radius:50%; background-color:${color}; display:inline-block;"></span>
              ${label}
            </div>
            <div style="color:#4b5563; margin-bottom:2px;">Amount: <strong style="color:#111827; font-weight:700;">Rs ${formatNumber(val)}</strong></div>
            <div style="color:#6b7280; font-size:11px;">Claim on Assets: <strong style="color:#2563eb;">${pct}%</strong></div>
          </div>
        `;
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Assets",
              fontSize: "12px",
              color: "#6b7280",
              formatter: () => `Rs ${formatNumber(totalAssets)}`,
            },
          },
        },
      },
    },
  };

  return (
    <div className="assets-liabilities-card">
      {/* Header */}
      <div className="assets-liabilities-header mb-2">
        <div>
          <div className="d-flex align-items-center gap-1">
            <h5 className="assets-liabilities-title m-0 mr-1">
              Assets vs Liabilities
            </h5>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="assets-vs-liabilities-info-tooltip">
                  Balance Sheet snapshot comparing business Assets against Liabilities and Owner's Equity (Assets = Liabilities + Owner's Equity).
                </Tooltip>
              }
            >
              <span className="ms-1" style={{ cursor: "pointer" }}>
                <FiInfo className="text-muted" style={{ fontSize: "14px" }} />
              </span>
            </OverlayTrigger>
          </div>
          <span className="assets-liabilities-subtitle">
            Quick snapshot {data?.as_of_date ? `(As of ${data.as_of_date})` : ""}
          </span>
        </div>
        <div className="btn-group btn-group-sm" role="group">
          <OverlayTrigger placement="top" overlay={<Tooltip id="assets-bar-tt">Bar Chart View</Tooltip>}>
            <button
              type="button"
              className={`btn btn-sm view-toggle-btn ${viewType === "bar" ? "active" : ""}`}
              onClick={() => setViewType("bar")}
            >
              <FiBarChart2 />
            </button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip id="assets-donut-tt">Donut Chart View</Tooltip>}>
            <button
              type="button"
              className={`btn btn-sm view-toggle-btn ${viewType === "donut" ? "active" : ""}`}
              onClick={() => setViewType("donut")}
            >
              <FiPieChart />
            </button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip id="assets-list-tt">Table Breakdown View</Tooltip>}>
            <button
              type="button"
              className={`btn btn-sm view-toggle-btn ${viewType === "list" ? "active" : ""}`}
              onClick={() => setViewType("list")}
            >
              <FiList />
            </button>
          </OverlayTrigger>
        </div>
      </div>

      {/* Compact Summary Metrics Bar */}
      {!isLoading && (
        <div
          className="d-flex align-items-center justify-content-between gap-2 py-1 px-2 mb-2 bg-light rounded border"
          style={{ fontSize: "12px" }}
        >
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted">Equity:</span>
            <span className="fw-bold text-success">{equityRatio}%</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted">Debt:</span>
            <span className="fw-bold" style={{ color: "#1e293b" }}>{debtRatio}%</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted">Net Eq:</span>
            <span className="fw-bold text-warning">Rs {formatNumber(ownersEquity)}</span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="assets-liabilities-body">
        {isLoading ? (
          <div>
            {[100, 45, 75].map((w, idx) => (
              <div key={idx} className="assets-liabilities-row mb-3">
                <div
                  className="finance-skeleton-box"
                  style={{ width: "125px", height: "14px", flexShrink: 0 }}
                />
                <div className="assets-liabilities-bar-track">
                  <div
                    className="finance-skeleton-box"
                    style={{ width: `${w}%`, height: "24px", borderRadius: "6px" }}
                  />
                </div>
                <div
                  className="finance-skeleton-box"
                  style={{ width: "110px", height: "14px", flexShrink: 0 }}
                />
              </div>
            ))}
          </div>
        ) : viewType === "bar" ? (
          <div style={{ width: "100%", height: "210px" }}>
            <Chart options={optionsBar} series={seriesBar} type="bar" height="210" width="100%" />
          </div>
        ) : viewType === "donut" ? (
          <div style={{ width: "100%", height: "210px" }}>
            <Chart options={optionsDonut} series={seriesDonut} type="donut" height="210" width="100%" />
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: "210px" }}>
            <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "12px" }}>
              <thead className="table-light">
                <tr>
                  <th>Financial Component</th>
                  <th className="text-end">Amount (Rs)</th>
                  <th className="text-end">Ratio %</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">
                    <span className="d-inline-block rounded-circle mr-2" style={{ width: 8, height: 8, backgroundColor: "#16a34a" }} />
                    <span className="fw-semibold text-dark">Total Assets</span>
                  </td>
                  <td className="text-end fw-bold text-dark py-2">Rs {formatNumber(totalAssets)}</td>
                  <td className="text-end text-success fw-bold py-2">100.0%</td>
                </tr>
                <tr>
                  <td className="py-2">
                    <span className="d-inline-block rounded-circle mr-2" style={{ width: 8, height: 8, backgroundColor: "#1e293b" }} />
                    <span className="fw-semibold text-dark">Total Liabilities</span>
                  </td>
                  <td className="text-end fw-bold text-dark py-2">Rs {formatNumber(totalLiabilities)}</td>
                  <td className="text-end text-muted py-2">{debtRatio}%</td>
                </tr>
                <tr>
                  <td className="py-2">
                    <span className="d-inline-block rounded-circle mr-2" style={{ width: 8, height: 8, backgroundColor: "#f59e0b" }} />
                    <span className="fw-semibold text-dark">Owner's Equity</span>
                  </td>
                  <td className="text-end fw-bold text-dark py-2">Rs {formatNumber(ownersEquity)}</td>
                  <td className="text-end text-warning fw-bold py-2">{equityRatio}%</td>
                </tr>
              </tbody>
              <tfoot className="table-light fw-bold">
                <tr>
                  <td>Total Liabilities & Equity</td>
                  <td className="text-end text-primary">Rs {formatNumber(totalLiabilities + ownersEquity)}</td>
                  <td className="text-end">100.0%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetsVsLiabilitiesWidget;
